import type { Workspace } from '@/lib/api';
import {
  WHATSAPP_BUSINESS_INTEGRATION_URL,
  whatsappBusinessIntegrationUrl,
} from '@/lib/whatsapp/businessAccountRoute';

export const MANAGE_WORKSPACES_URL = '/home/workspaces';

export const WORKSPACE_SETTINGS_URL = '/home/workspace-settings';
export const WORKSPACE_BRAND_WEBSITES_URL = `${WORKSPACE_SETTINGS_URL}?section=brand-websites`;
export const WORKSPACE_BUSINESS_ACCOUNTS_URL = WHATSAPP_BUSINESS_INTEGRATION_URL;
export const WORKSPACE_MCP_URL = `${WORKSPACE_SETTINGS_URL}?section=mcp`;
export const WORKSPACE_AI_KNOWLEDGE_URL = `${WORKSPACE_SETTINGS_URL}?section=ai-knowledge`;
export const TEAM_MEMBERS_URL = '/home/team-management';

export const MOVED_WORKSPACE_SETTING_SECTIONS = [
  'workspace',
  'business-accounts',
  'mcp',
  'ai-knowledge',
] as const;

export function isMovedWorkspaceSettingSection(raw: string | null): boolean {
  return (MOVED_WORKSPACE_SETTING_SECTIONS as readonly string[]).includes(raw || '');
}

export function workspaceSettingsRedirectFromAccount(search: URLSearchParams): string {
  const section = search.get('section');
  const next = new URLSearchParams();
  if (section === 'business-accounts') {
    const tab = search.get('tab');
    if (tab) next.set('tab', tab);
    return whatsappBusinessIntegrationUrl(next);
  }
  if (section === 'workspace') next.set('section', 'brand-websites');
  else if (section && section !== 'profile') next.set('section', section);
  const tab = search.get('tab');
  if (tab) next.set('tab', tab);
  const qs = next.toString();
  return qs ? `${WORKSPACE_SETTINGS_URL}?${qs}` : WORKSPACE_SETTINGS_URL;
}

export type WorkspaceRoleLabel = 'Owner' | 'Admin' | 'Member';

export type WorkspaceRoleUser = {
  role?: string | null;
  is_organization_owner?: boolean | null;
  is_super_admin?: boolean | null;
} | null | undefined;

export function workspaceRoleLabel(user: WorkspaceRoleUser): WorkspaceRoleLabel {
  if (user?.is_organization_owner) return 'Owner';
  const role = (user?.role || '').toString().trim().toUpperCase();
  if (user?.is_super_admin || role === 'ADMIN') return 'Admin';
  return 'Member';
}

export function isOrgAdmin(user: WorkspaceRoleUser): boolean {
  if (!user) return false;
  if (user.is_super_admin || user.is_organization_owner) return true;
  return (user.role || '').toString().trim().toUpperCase() === 'ADMIN';
}

export function canOpenWorkspaceSettings(user: WorkspaceRoleUser): boolean {
  return isOrgAdmin(user);
}

export function canOpenTeamMembers(user: WorkspaceRoleUser): boolean {
  return isOrgAdmin(user);
}

export function canCreateWorkspace(user: WorkspaceRoleUser): boolean {
  return isOrgAdmin(user);
}

export function canSetDefaultWorkspace(user: WorkspaceRoleUser): boolean {
  return isOrgAdmin(user);
}

export function canUseManageWorkspacesPage(user: WorkspaceRoleUser): boolean {
  if (!user) return false;
  if (user.is_super_admin || user.is_organization_owner) return true;
  const role = (user.role || '').toString().trim().toUpperCase();
  return role === 'ADMIN' || role === 'USER' || role === 'AGENT';
}

export function workspaceTableSubtitle(workspace: Pick<Workspace, 'whatsapp'> | null | undefined): string {
  const phone = (workspace?.whatsapp?.phone_number || '').trim();
  return phone || 'WeNext workspace';
}

export function workspaceInitial(name: string | undefined): string {
  const letter = (name || '').trim().charAt(0);
  return letter ? letter.toUpperCase() : 'W';
}

export function formatTeamMemberCount(count: number | null | undefined): string {
  if (count == null || !Number.isFinite(count) || count < 0) return '—';
  const n = Math.floor(count);
  return n === 1 ? '1 member' : `${n} members`;
}
